const THREE = require("three");
const UTILS = require(/*! ./utils.js */"./utils.js");
const SimplexNoise = require(/*! simplex-noise */"../../kru_node_modules/simplex-noise/simplex-noise.js");
const easing = require(/*! ./easing.js */"./easing.js").easing;
let isClient = typeof location != "undefined";
let TERRAIN_MATERIAL;
if (isClient) {
  TERRAIN_MATERIAL = new THREE.StrippedLambertMaterial({
    color: 0x8c8c8c,
    flatShading: false,
    vertexColors: THREE.VertexColors
  });

  // DEBUG:
  // TERRAIN_MATERIAL = new THREE.MeshBasicMaterial({
  //     color: 0xffffff,
  //     vertexColors: THREE.VertexColors
  // });
}
const BIOMES = {
  GRASS: 0,
  MOUNTAIN: 1
};
const BIOME_COUNT = Object.keys(BIOMES).length;
const BIOME_COLORS = {
  [BIOMES.GRASS]: [56, 226, 102],
  [BIOMES.MOUNTAIN]: [160, 160, 160]
};
const COLORS = {
  DIRT: [140, 104, 53],
  // #8c6835
  GRASS: [155, 186, 46],
  // #4a6904
  MOUNTAIN: [160, 160, 160] // #a0a0a0
};
const hexToRGB = hex => hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => '#' + r + r + g + g + b + b).substring(1).match(/.{2}/g).map(x => parseInt(x, 16));
const TARGET_SEGMENT_SIZE = 60;
const SIZE_MLT = 3;
class Terrain extends THREE.Object3D {
  static fromConfig(config) {
    return new Terrain(config.terrainSeed, config.terrainWidth, config.terrainHeight, config.sizeMlt, config.terrainMntMlt, config.terrainDrtCol, config.terrainGrsCol, config.terrainMntCol);
  }
  constructor(seed, sizeX, sizeY, sizeMlt, mntMlt, drtCol, grsCol, mntCol) {
    super();

    // SETUP:
    sizeMlt = SIZE_MLT;
    this.sizeMlt = sizeMlt;
    // sizeX /= sizeMlt;
    // sizeY /= sizeMlt;
    this.sizeX = sizeX;
    this.sizeY = sizeY;
    this.edgeSize = 500;
    this.mntMlt = mntMlt;
    COLORS.DIRT = hexToRGB(drtCol);
    COLORS.MOUNTAIN = hexToRGB(mntCol);
    COLORS.GRASS = hexToRGB(grsCol);
    this.simplex = new SimplexNoise(seed);

    // CREATE GEO:
    let xSegmentCount = Math.floor(sizeX / TARGET_SEGMENT_SIZE);
    let ySegmentCount = Math.floor(sizeY / TARGET_SEGMENT_SIZE);
    this.xSegmentCount = xSegmentCount;
    this.ySegmentCount = ySegmentCount;
    let xSegmentSize = sizeX / xSegmentCount;
    let ySegmentSize = sizeY / ySegmentCount;
    this.xSegmentSize = xSegmentSize;
    this.ySegmentSize = ySegmentSize;
    let baseGeo = new THREE.BufferGeometry();

    // ADD ATTRIBUTES:
    let totalSegmentCount = xSegmentCount * ySegmentCount;
    let vertCount = totalSegmentCount * 6; // 6 verts per segment (since we need to color each tri differently)
    let positions = new THREE.BufferAttribute(new Float32Array(vertCount * 3), 3);
    baseGeo.addAttribute("position", positions);
    let colors = new THREE.BufferAttribute(new Uint8Array(vertCount * 3), 3, true);
    baseGeo.addAttribute("color", colors);

    // STORE ATTRIBUTES FOR QUICK ACCESS:
    this.vertPositions = positions;

    // TERRAIN HEIGHTS: (we cache this for performance)
    this.terrainPoints = (xSegmentCount + 1) * (ySegmentCount + 1);
    this.terrainHeights = new Float32Array(this.terrainPoints);
    this.terrainBiomes = new Float32Array(this.terrainPoints * BIOME_COUNT);
    for (let xSeg = 0; xSeg < xSegmentCount + 1; xSeg++) {
      for (let ySeg = 0; ySeg < ySegmentCount + 1; ySeg++) {
        let i = xSeg + ySeg * (xSegmentCount + 1); // Index inside mesh
        let x = this._vertPos(xSeg, xSegmentSize, sizeX);
        let y = this._vertPos(ySeg, ySegmentSize, sizeY);
        let [height, biomes] = this.evaluate(x, y);

        // Set height
        this.terrainHeights[i] = height;

        // Save the biomes
        this.terrainBiomes.set(biomes, i * BIOME_COUNT);
      }
    }

    // UPDATE ATTRIBUTES:
    for (let xSeg = 0; xSeg < xSegmentCount; xSeg++) {
      for (let ySeg = 0; ySeg < ySegmentCount; ySeg++) {
        // GET PROPERTIES:
        let i = xSeg + ySeg * xSegmentCount; // Index inside mesh
        let i6 = i * 6;
        let x = this._vertPos(xSeg, xSegmentSize, sizeX);
        let y = this._vertPos(ySeg, ySegmentSize, sizeY);

        // GET POSITIONS:
        let p0 = [x, y, this.terrainHeight(xSeg, ySeg)];
        let p1 = [x + xSegmentSize, y, this.terrainHeight(xSeg + 1, ySeg)];
        let p2 = [x + xSegmentSize, y + ySegmentSize, this.terrainHeight(xSeg + 1, ySeg + 1)];
        let p3 = [x, y + ySegmentSize, this.terrainHeight(xSeg, ySeg + 1)];

        // GET AO:
        let ao0 = this.calcAOWeight(xSeg, ySeg);
        let ao1 = this.calcAOWeight(xSeg + 1, ySeg);
        let ao2 = this.calcAOWeight(xSeg + 1, ySeg + 1);
        let ao3 = this.calcAOWeight(xSeg, ySeg + 1);
        // ao0 = ao1 = ao2 = ao3 = 0;  // TEMP: Test shading

        // SET POSITIONS
        positions.setXYZ(i6, ...p0);
        positions.setXYZ(i6 + 1, ...p1);
        positions.setXYZ(i6 + 2, ...p2);
        positions.setXYZ(i6 + 3, ...p2);
        positions.setXYZ(i6 + 4, ...p3);
        positions.setXYZ(i6 + 5, ...p0);

        // GET CENTROIDS:
        let centroidA = this._centroid3D(p0, p1, p2);
        let centroidB = this._centroid3D(p2, p3, p0);
        let normalA = this._normal3D(p0, p1, p2);
        let normalB = this._normal3D(p2, p3, p0);
        let biomeA = this.terrainBiomeAt(centroidA);
        let biomeB = this.terrainBiomeAt(centroidB);
        let colorA = this.evaluateColor(centroidA, normalA, biomeA);
        let colorB = this.evaluateColor(centroidB, normalB, biomeB);

        // SET PER TRIANGLE COLOR:
        colors.setXYZ(i6, ...this._weightColor(colorA, ao0));
        colors.setXYZ(i6 + 1, ...this._weightColor(colorA, ao1));
        colors.setXYZ(i6 + 2, ...this._weightColor(colorA, ao2));
        colors.setXYZ(i6 + 3, ...this._weightColor(colorB, ao2));
        colors.setXYZ(i6 + 4, ...this._weightColor(colorB, ao3));
        colors.setXYZ(i6 + 5, ...this._weightColor(colorB, ao0));
      }
    }
    if (isClient) {
      // COMPUTE NORMALS:
      baseGeo.computeVertexNormals(true);
      baseGeo.computeFaceNormals();

      // ADD MESH:
      this.baseMesh = new THREE.Mesh(baseGeo, TERRAIN_MATERIAL);
      this.baseMesh.receiveShadow = true;
      this.baseMesh.scale.set(sizeMlt, sizeMlt, 1);
      this.add(this.baseMesh);
    }

    // RAYCAST UTILS:
    this._raycastRay = new THREE.Ray();
    this._raycastTriA = new THREE.Vector3();
    this._raycastTriB = new THREE.Vector3();
    this._raycastTriC = new THREE.Vector3();
    this._raycastTriangle = new THREE.Triangle();
    this._raycastNormal = new THREE.Vector3();
    this._raycastTarget = new THREE.Vector3();
    this._raycastClosestTarget = new THREE.Vector3();
  }
  terrainIndex(xSeg, ySeg) {
    let index = xSeg + ySeg * (this.xSegmentCount + 1);
    if (index < 0 || index >= this.terrainHeights.length) {
      return 0;
    }
    return index;
  }
  terrainHeight(xSeg, ySeg) {
    // TERRAIN HEIGHT FOR SEGMENT:
    return this.terrainHeights[this.terrainIndex(xSeg, ySeg)];
  }
  terrainBiome(xSeg, ySeg) {
    // TERRAIN BIOME FOR SEGMENT:
    let startIndex = this.terrainIndex(xSeg, ySeg) * BIOME_COUNT;
    return this.terrainBiomes.slice(startIndex, startIndex + BIOME_COUNT);
  }
  terrainBiomeAt(x, y) {
    let xSeg0 = Math.floor(x / this.xSegmentSize);
    let xSeg1 = Math.ceil(x / this.xSegmentSize);
    let ySeg0 = Math.floor(y / this.ySegmentSize);
    let ySeg1 = Math.ceil(y / this.ySegmentSize);
    let b0 = this.terrainBiome(xSeg0, ySeg0);
    let b1 = this.terrainBiome(xSeg1, ySeg0);
    let b2 = this.terrainBiome(xSeg0, ySeg1);
    let b3 = this.terrainBiome(xSeg1, ySeg1);
    return (b0 + b1 + b2 + b3) / 4;
  }
  calcAOWeight(xSeg, ySeg) {
    // GET VERT POSITION:
    let x = this._vertPos(xSeg, this.xSegmentSize, this.sizeX);
    let y = this._vertPos(ySeg, this.ySegmentSize, this.sizeY);

    // GET ANGLE ON TOP SIDE OF TERRAIN:
    let xAngle = this._topAngleOnTerrain(x - this.xSegmentSize, this.terrainHeight(xSeg - 1, ySeg), x, this.terrainHeight(xSeg, ySeg), x + this.xSegmentSize, this.terrainHeight(xSeg + 1, ySeg));
    let yAngle = this._topAngleOnTerrain(y - this.xSegmentSize, this.terrainHeight(xSeg, ySeg - 1), y, this.terrainHeight(xSeg, ySeg), y + this.ySegmentSize, this.terrainHeight(xSeg, ySeg + 1));

    // CALCULATE WEIGHTS:
    let compAngle = Math.PI;
    let xWeight = Math.max(compAngle - xAngle, 0) / compAngle;
    let yWeight = Math.max(compAngle - yAngle, 0) / compAngle;

    // COMBINE WEIGHTS:
    let tmpW = 1 - (1 - xWeight) * (1 - yWeight);
    if (tmpW >= 0.5) {
      tmpW = 0.65;
    } else {
      if (tmpW >= 0.2) {
        tmpW = 0.3;
      } else {
        if (tmpW >= 0.1) {
          tmpW = 0.25;
        }
      }
    }
    return tmpW;
  }

  // heightAt(x, y) {
  //     // TODO: IT HAS TO FACTOR IN THE TRIANGLES
  //
  //     // INTERPOLATED HEIGHT FOR POSITION:
  //
  //     x /= this.sizeMlt;
  //     y /= this.sizeMlt;
  //
  //     let xSeg = Math.floor(this._worldToLocal(x, this.sizeX, this.xSegmentSize));
  //     let ySeg = Math.floor(this._worldToLocal(y, this.sizeY, this.ySegmentSize));
  //
  //     let progressX = modFix(this._worldToLocal(x), this.xSegmentSize) / this.xSegmentSize;
  //     let progressY = modFix(this._worldToLocal(y), this.ySegmentSize) / this.ySegmentSize;
  //
  //     // SAMPLE LEFT AND RIGHT SIDE OF QUAD:
  //     let leftLerp = lerp(this.terrainHeight(xSeg, ySeg), this.terrainHeight(xSeg, ySeg + 1), progressY);
  //     let rightLerp = lerp(this.terrainHeight(xSeg + 1, ySeg), this.terrainHeight(xSeg + 1, ySeg + 1), progressY);
  //
  //     return lerp(leftLerp, rightLerp, progressX);
  // }
  //
  // normalAt(x, y) {
  //     // TODO: SIZE MULT
  //
  //     let xSeg0 = Math.floor(x / this.xSegmentSize);
  //     let ySeg0 = Math.floor(y / this.ySegmentSize);
  //     let xSeg1 = xSeg0 + 1;
  //     let ySeg1 = ySeg0 + 1;
  //
  //     let progressX = modFix(x, this.xSegmentSize) / this.xSegmentSize;
  //     let progressY = modFix(y, this.ySegmentSize) / this.ySegmentSize;
  //
  //     // let x0 = this.heigh
  // }

  _worldToLocal(v, sizeV, vSegmentSize) {
    // ASSUMES ALREADY DIVIDED `v` by `this.sizeMlt`
    return (v + sizeV / 2) / vSegmentSize;
  }
  raycast(oX, oY, oZ, dX, dY, dZ, calcNormal = false) {
    // CONVERT COORDINATE SPACE FOR X AND Y:
    oX /= this.sizeMlt;
    oY /= this.sizeMlt;
    dX /= this.sizeMlt;
    dY /= this.sizeMlt;

    // SETUP DATA:
    let maxDist = Math.sqrt(dX * dX + dY * dY + dZ * dZ);
    this._raycastRay.origin.set(oX, oY, oZ);
    this._raycastRay.direction.set(dX, dY, dZ);

    // CONVERT PROPERTIES TO WORK WITH CODE:
    let x0 = this._worldToLocal(oX, this.sizeX, this.xSegmentSize);
    let y0 = this._worldToLocal(oY, this.sizeY, this.ySegmentSize);
    let x1 = x0 + dX / this.xSegmentSize;
    let y1 = y0 + dY / this.ySegmentSize; // FROM: http://playtechs.blogspot.com/2007/03/raytracing-on-grid.html
    let sdx = Math.abs(x1 - x0);
    let sdy = Math.abs(y1 - y0);
    let x = Math.floor(x0);
    let y = Math.floor(y0);
    let n = 1;
    let xInc;
    let yInc;
    let error;
    if (sdx == 0) {
      xInc = 0;
      error = Number.POSITIVE_INFINITY;
    } else {
      if (x1 > x0) {
        xInc = 1;
        n += Math.floor(x1) - x;
        error = (Math.floor(x0) + 1 - x0) * sdy;
      } else {
        xInc = -1;
        n += x - Math.floor(x1);
        error = (x0 - Math.floor(x0)) * sdy;
      }
    }
    if (sdy == 0) {
      yInc = 0;
      error -= Number.POSITIVE_INFINITY;
    } else {
      if (y1 > y0) {
        yInc = 1;
        n += Math.floor(y1) - y;
        error -= (Math.floor(y0) + 1 - y0) * sdx;
      } else {
        yInc = -1;
        n += y - Math.floor(y1);
        error -= (y0 - Math.floor(y0)) * sdx;
      }
    }
    for (; n > 0; n--) {
      // VISIT CELL:
      let foundTri = this._raycastVisit(x, y, oX, oY, oZ, dX, dY, dZ, maxDist, calcNormal);
      if (foundTri) {
        // CONVERT COORDINATE SPACE:
        let result = this._raycastClosestTarget;
        result.x *= this.sizeMlt;
        result.y *= this.sizeMlt;
        return result;
      }

      // STEP BLOCK:
      if (error > 0) {
        y += yInc;
        error -= sdx;
      } else {
        x += xInc;
        error += sdy;
      }
    }
  }
  _raycastVisit(xSeg, ySeg, x0, y0, z0, x1, y1, z1, maxDist, calcNormal) {
    // CALCULATE INDEXES:
    let baseI = xSeg + ySeg * this.xSegmentCount; // Index inside mesh

    // FIND CLOSEST POINT:
    let closestDist = Number.POSITIVE_INFINITY;
    let foundClosest = false;
    for (let j = 0; j < 2; j++) {
      // GET INDEX FOR INDIVIDUAL TRIANGLE:
      let i = baseI * 6 + j * 3;

      // SETUP DATA:
      this._raycastTriA.set(this.vertPositions.getX(i), this.vertPositions.getY(i), this.vertPositions.getZ(i));
      this._raycastTriB.set(this.vertPositions.getX(i + 1), this.vertPositions.getY(i + 1), this.vertPositions.getZ(i + 1));
      this._raycastTriC.set(this.vertPositions.getX(i + 2), this.vertPositions.getY(i + 2), this.vertPositions.getZ(i + 2));

      // PERFORM INTERSECTION:
      let result = this._raycastRay.intersectTriangle(this._raycastTriA, this._raycastTriB, this._raycastTriC, true, this._raycastTarget);
      if (result) {
        let dist = result.distanceTo(this._raycastRay.origin);
        if (dist < closestDist && dist < maxDist) {
          closestDist = dist;
          foundClosest = true;
          this._raycastClosestTarget.copy(result);
          this._raycastTriangle.set(this._raycastTriA, this._raycastTriB, this._raycastTriC);
        }
      }
    }

    // CALC NORMAL IF NEEDED:
    if (calcNormal && foundClosest) {
      this._raycastTriangle.getNormal(this._raycastNormal);
    }
    return foundClosest;
  }
  evaluate(x, y) {
    let v = 0;
    this.sampleIndex = 0;

    // Get samples and convert to percentages
    let biomeSamples = [];
    for (let i = 0; i < BIOME_COUNT; i++) {
      biomeSamples.push(this._sample(x, y, 1300));
    }
    // biomeSamples[BIOMES.GRASS] = 0;
    // biomeSamples[BIOMES.MOUNTAIN] = 1;
    // biomeSamples[BIOMES.DESERT] = 0;
    let biomeSum = biomeSamples.reduce((prev, curr) => prev + curr, 0);
    biomeSamples = biomeSamples.map((v, i) => [i, v / biomeSum]); // Map to % total tagged with biome index
    biomeSamples = biomeSamples.sort((a, b) => b[1] - a[1]);

    // Ease biomes  // TODO: Just average the top two and discard the third? Saves perf so we don't do noise for unneeded values
    let biomes = {};
    let [firstI, firstV] = biomeSamples[0];
    biomes[firstI] = this._easeTerrain(firstV / (firstV + biomeSamples[1][1])); // Get percent between first and second
    for (let i = 1; i < biomeSamples.length; i++) {
      let [currI, currV] = biomeSamples[i];
      biomes[currI] = this._easeTerrain(currV / (firstV + currV));
    }

    // Grass
    let grassSample = this._sample(x, y, 300);
    let grassSteps = 3;
    grassSample = Math.floor(grassSample * grassSteps) / grassSteps;
    v += grassSample * 25 * biomes[BIOMES.GRASS] * this.sizeMlt;

    // Mountain
    let mRidgeCenter = 0.4 + this._sample(x, y, 200) * 0.3; // Find the center of the ridge; this gives it a bit more wavy and hilly effect
    let mRidgeDistance = 1 - Math.abs(mRidgeCenter - this._sample(x, y, 500)) * 2; // Create sharp tops (combined with log later)
    let mEmphasis = 0.4 + this._sample(x, y, 400) * 1.4;
    v += mRidgeDistance * mEmphasis * (220 * this.mntMlt) * biomes[BIOMES.MOUNTAIN] * this.sizeMlt;

    // Edge fade
    let fade = this._calcEdgeFade(x, this.sizeX) * this._calcEdgeFade(y, this.sizeY);
    v *= fade; // Remove terrain features
    v += (fade - 1) * 2000; // Sink below the ocean
    if (Math.abs(x) >= this.sizeX / 2 || Math.abs(y) >= this.sizeY / 2) {
      v = -1000;
    }
    return [v, biomes];
  }
  evaluateColor(centroid, normal, biome) {
    // CALC ANGLE:
    let height = centroid[2];
    let xAngle = Math.atan2(Math.abs(normal[0]), Math.abs(normal[2]));
    let yAngle = Math.atan2(Math.abs(normal[1]), Math.abs(normal[2]));

    // CALC COLOR:
    let xShade = 1 - xAngle / (Math.PI * 2);
    let yShade = 1 - yAngle / (Math.PI * 2);
    let slopeShade = xShade * yShade;
    let color;
    if (slopeShade <= 0.85) {
      color = COLORS.MOUNTAIN;
    } else {
      if (height > 100) {
        color = COLORS.MOUNTAIN;
      } else {
        if (height > 5) {
          color = COLORS.GRASS;
        } else {
          color = COLORS.DIRT;
        }
      }
    }

    // color = [0, slopeShade * 255, 0];

    return color;
  }
  _sample(x, y, divisor) {
    this.sampleIndex++;
    return this.simplex.noise3D(x / divisor, y / divisor, this.sampleIndex * 10000) / 2 + 0.5;
  }
  _easeTerrain(v) {
    return easing.easeInOutQuint(easing.easeInOutQuint(v));
  }
  _calcEdgeFade(x, size) {
    if (Math.abs(x) >= size / 2) {
      return 0;
    } // Don't do anything beyond the edge
    let fade = (Math.abs(x) - size / 2 + this.edgeSize) / this.edgeSize;
    fade = 1 - Math.max(fade, 0);
    return easing.easeOutQuint(fade);
  }
  _blendColors(biomes) {
    // Get the total biome weight
    let biomeTotal = Object.values(biomes).reduce((prev, curr) => prev + curr, 0);

    // Blend the colors
    let color = [0, 0, 0];
    for (let biomeId in biomes) {
      let biomeColor = BIOME_COLORS[biomeId];
      let biomeWeight = biomes[biomeId] / biomeTotal;
      for (let i = 0; i < 3; i++) {
        color[i] += biomeColor[i] * biomeWeight;
      }
    }
    return color;
  }
  _vertPos(vIndex, vSegmentSize, sizeV) {
    return vIndex * vSegmentSize - sizeV / 2;
  }
  _avgPos(pointA, pointB) {
    return [(pointA[0] + pointB[0]) / 2, (pointA[1] + pointB[1]) / 2, (pointA[2] + pointB[2]) / 2];
  }
  _centroid3D(a, b, c) {
    let centerX = (a[0] + b[0] + c[0]) / 3;
    let centerY = (a[1] + b[1] + c[1]) / 3;
    let centerZ = (a[2] + b[2] + c[2]) / 3;
    return [centerX, centerY, centerZ];
  }
  _normal3D(a, b, c) {
    // C - B:
    let cbX = c[0] - b[0];
    let cbY = c[1] - b[1];
    let cbZ = c[2] - b[2];

    // A - B:
    let abX = a[0] - b[0];
    let abY = a[1] - b[1];
    let abZ = a[2] - b[2];

    // CB X AB
    let nX = cbY * abZ - cbZ * abY;
    let nY = cbZ * abX - cbX * abZ;
    let nZ = cbX * abY - cbY * abX;

    // NORMALIZE:
    let L = Math.sqrt(nX * nX + nY * nY + nZ + nZ);
    return [nX / L, nY / L, nZ / L];
  }
  _topAngleOnTerrain(x0, y0, x1, y1, x2, y2) {
    let atanA = Math.atan2(y0 - y1, x0 - x1);
    let atanB = Math.atan2(y2 - y1, x2 - x1);
    if (atanA < 0) {
      atanA += Math.PI * 2;
    }
    return atanA - atanB;
  }
  _weightColor(color, weight) {
    let m = 1 - weight;
    return color.map(v => v * m);

    // DEBUG MATCAP:
    // m *= 255;
    // return [m, m, m];
  }
}
function modFix(a, b) {
  return (a % b + b) % b;
}
function lerp(v0, v1, t) {
  return v0 * (1 - t) + v1 * t;
}
module.exports = Terrain;